/**
 * Quote-reply, carried as CONTENT and nothing else (owner decision 2026-08-22).
 *
 * The obvious way to build this is a `reply_to` column: a message id on the
 * row, resolved at render. It is also the wrong way here, and the reason is
 * the doorman.
 *
 * EVERY MESSAGE ON THIS PRODUCT IS READ BY A MODEL. A quote that lives in a
 * foreign key is a quote the model never sees — it would read "no, the second
 * one" with no idea what the second one was, and answer accordingly. A quote
 * that lives in the text is a quote the model reads exactly as the recipient
 * does, because it IS what the human sent. There is no second channel to keep
 * in step, no schema change, no join, and nothing for a future migration to
 * drop on the floor.
 *
 * So the wire format is a quotation, spelled the way people have spelled
 * quotations in plain text for forty years:
 *
 *     > Nob wrote:
 *     > What is it you actually want from her?
 *
 *     A twenty-minute call about the Series B.
 *
 * The renderer picks that prefix back off and draws it as a quote bar. If the
 * parse ever fails — a sender typed their own angle brackets, a format drifts —
 * the failure mode is a message that renders as the plain text it always was.
 * Nothing is lost and nothing is guessed, which is the same posture as every
 * other parse in this system.
 */

/** Longer than this and a quote bar is quoting the whole conversation back. */
const MAX_QUOTE = 180;

/**
 * One line, tidied. A quote bar is a reminder of what is being answered, not a
 * reproduction of it, so newlines collapse and the tail is cut with an ellipsis
 * rather than wrapped — the full text is still up there in the thread.
 */
export function trimQuote(text: string): string {
  const flat = text.replace(/\s+/g, " ").trim();
  return flat.length > MAX_QUOTE ? `${flat.slice(0, MAX_QUOTE - 1).trimEnd()}…` : flat;
}

export type Quote = { speaker: string; text: string };

/**
 * The quote as it goes onto the wire, ahead of what was typed.
 *
 * The blank line matters: it is what separates the quotation from the reply
 * for a reader, for the parser below, and for the model. Without it the two
 * run together into one paragraph that begins with somebody else's words.
 */
export function withQuote(quote: Quote, body: string): string {
  const excerpt = trimQuote(quote.text);
  if (!excerpt) return body;
  return `> ${quote.speaker} wrote:\n> ${excerpt}\n\n${body}`;
}

const QUOTED_LINE = /^>[ \t]?(.*)$/;
const SPEAKER = /^(.{1,60}) wrote:$/;

/**
 * Split a message back into the quote it carries and the words that answered
 * it. A message with no quotation comes back whole, with `quote: null` — which
 * is every message this product has ever stored, and most of the ones it will.
 */
export function parseQuoted(content: string): { quote: Quote | null; body: string } {
  if (!content.startsWith(">")) return { quote: null, body: content };

  const lines = content.split("\n");
  const quoted: string[] = [];
  let i = 0;
  for (; i < lines.length; i++) {
    const match = QUOTED_LINE.exec(lines[i]!);
    if (!match) break;
    quoted.push(match[1]!);
  }
  if (quoted.length === 0) return { quote: null, body: content };

  // A leading "Someone wrote:" names the voice; without one the quotation is
  // still a quotation, just an unattributed one. Neither is an error.
  const speakerMatch = SPEAKER.exec(quoted[0]!.trim());
  const speaker = speakerMatch ? speakerMatch[1]!.trim() : "";
  const text = (speakerMatch ? quoted.slice(1) : quoted).join(" ").replace(/\s+/g, " ").trim();

  // A prefix that quoted nothing is not a quote — it is a line that began with
  // an angle bracket, and it belongs in the body where the sender put it.
  if (!text) return { quote: null, body: content };

  // The blank line the format writes is the separator, not part of the reply.
  const rest = lines.slice(i);
  while (rest.length > 0 && rest[0]!.trim() === "") rest.shift();

  return { quote: { speaker, text }, body: rest.join("\n") };
}
